import { useEffect, useRef } from 'react';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';

/**
 * Compact field editor for a single annotation attribute cell in the table.
 * Enter saves the cell; Escape cancels inline edit.
 */
export function TableCellFieldInput({
  field,
  value,
  onChange,
  onEnter,
  onEscape,
  autoFocus = true,
}) {
  const inputRef = useRef(null);

  useEffect(() => {
    if (autoFocus && inputRef.current?.focus) {
      inputRef.current.focus();
      if (inputRef.current.select) {
        inputRef.current.select();
      }
    }
  }, [autoFocus, field.name]);

  const handleEscape = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onEscape?.();
    }
  };

  const handleKeyDownCapture = (e) => {
    if (e.key === 'Escape') {
      handleEscape(e);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      onEnter?.();
    }
  };

  const wrap = (node) => (
    <div
      style={{ width: '100%' }}
      onKeyDownCapture={handleKeyDownCapture}
      onClick={(e) => e.stopPropagation()}
    >
      {node}
    </div>
  );

  const commonSx = { width: '100%', fontSize: '0.875rem' };

  switch (field.type) {
    case 'select':
      return wrap(
        <Select
          native
          size="small"
          fullWidth
          autoFocus={autoFocus}
          inputRef={inputRef}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleEscape}
          sx={commonSx}
        >
          {field.options?.map(({ id, text }) => (
            <option key={id} value={id}>
              {text}
            </option>
          ))}
        </Select>
      );
    case 'number':
      return wrap(
        <TextField
          size="small"
          fullWidth
          type="number"
          inputRef={inputRef}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          inputProps={{ min: field.min, max: field.max }}
          sx={commonSx}
        />
      );
    case 'checkbox':
      return wrap(
        <Checkbox
          size="small"
          inputRef={inputRef}
          checked={value === true}
          onChange={(e) => onChange(e.target.checked)}
        />
      );
    case 'date':
      return wrap(
        <TextField
          size="small"
          fullWidth
          type="date"
          inputRef={inputRef}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          sx={commonSx}
        />
      );
    case 'datetime-local':
      return wrap(
        <TextField
          size="small"
          fullWidth
          type="datetime-local"
          inputRef={inputRef}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          sx={commonSx}
        />
      );
    case 'email':
      return wrap(
        <TextField
          size="small"
          fullWidth
          type="email"
          inputRef={inputRef}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          sx={commonSx}
        />
      );
    case 'textarea':
      return wrap(
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={2}
          inputRef={inputRef}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          sx={commonSx}
        />
      );
    case 'text':
    default:
      return wrap(
        <TextField
          size="small"
          fullWidth
          inputRef={inputRef}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          sx={commonSx}
        />
      );
  }
}
